/* eslint-disable no-unused-vars */
import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';

export interface KpiItem {
  id: string;
  label: string;
  value: string | number;
  hint?: string;
  delta?: string;
  deltaTone?: 'positive' | 'negative' | 'neutral';
}

interface KpiStripProps {
  items: KpiItem[];
  selectedId?: string;
  onSelect?: (id: string) => void;
}

const deltaColor = {
  positive: 'state.success',
  negative: 'state.destructive',
  neutral: 'content.faint',
};

export const KpiStrip = ({ items, selectedId, onSelect }: KpiStripProps) => (
  <Box
    display="grid"
    gridAutoFlow={{ base: 'row', md: 'column' }}
    gridAutoColumns={{ md: '1fr' }}
    gridTemplateColumns={{ base: '1fr', sm: '1fr 1fr', md: 'unset' }}
  >
    {items.map((item, index) => {
      const isSelected = item.id === selectedId;
      const interactive = Boolean(onSelect);

      return (
        <Flex
          as={interactive ? 'button' : 'div'}
          key={item.id}
          direction="column"
          gap="4px"
          flex="1"
          minW={0}
          p="16px"
          textAlign="left"
          cursor={interactive ? 'pointer' : 'default'}
          borderBottomWidth="2px"
          borderBottomStyle="solid"
          borderBottomColor={isSelected ? 'content.strong' : 'transparent'}
          borderRightWidth={{ base: '0', md: index === items.length - 1 ? '0' : '1px' }}
          borderRightStyle="solid"
          borderRightColor="line.hairline"
          borderTopWidth={{ base: index === 0 ? '0' : '1px', md: '0' }}
          borderTopStyle="solid"
          borderTopColor="line.hairline"
          _hover={interactive ? { bg: 'surface.frame' } : {}}
          aria-pressed={interactive ? isSelected : undefined}
          onClick={() => onSelect?.(item.id)}
        >
          <Text fontSize="12px" lineHeight="16px" color="content.subtle" noOfLines={1}>
            {item.label}
          </Text>
          <Flex align="baseline" justify="space-between" gap="8px">
            <Text
              fontFamily="display"
              fontSize="24px"
              lineHeight="30px"
              fontWeight={500}
              color="content.strong"
            >
              {item.value}
            </Text>
            {item.delta && (
              <Text
                fontFamily="mono"
                fontSize="12px"
                color={deltaColor[item.deltaTone ?? 'neutral']}
              >
                {item.delta}
              </Text>
            )}
          </Flex>
          <Box minH="16px">
            {item.hint && (
              <Text fontSize="12px" lineHeight="16px" color="content.body" noOfLines={1}>
                {item.hint}
              </Text>
            )}
          </Box>
        </Flex>
      );
    })}
  </Box>
);
